import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { faChartLine, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { formatDate } from 'helpers/formatDate';
import styles from './WorkTimeChart.scss';
import { Stats } from './Stats';

export const WorkTimeChart = ({ data, dateRange }) => {
  const chartData = useMemo(() => {
    const { workingDays } = data;
    const days = [];

    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = formatDate(d);
      const minutes = workingDays[dateStr] || 0;
      const hours = minutes / 60;

      days.push({
        date: dateStr,
        displayDate: formatDate(d, 'UA', true),
        minutes: minutes,
        hours: parseFloat(hours.toFixed(2)),
        dayName: d.toLocaleDateString('uk-UA', { weekday: 'short' }),
      });
    }

    return days;
  }, [data.workingDays, dateRange]);

  const weeklyData = useMemo(() => {
    if (chartData.length < 14) return [];

    const weeks = [];
    let currentWeek = [];
    let weekStart = null;

    const formatDateMonth = (date) => formatDate(date, 'UA', true);

    chartData.forEach((day, index) => {
      const dayDate = new Date(day.date);
      const dayOfWeek = dayDate.getDay();

      if (dayOfWeek === 1 || index === 0) {
        if (currentWeek.length > 0) {
          const weekTotal = currentWeek.reduce((sum, d) => sum + d.minutes, 0);
          weeks.push({
            weekStart: weekStart,
            weekEnd: currentWeek[currentWeek.length - 1].date,
            totalMinutes: weekTotal,
            totalHours: parseFloat((weekTotal / 60).toFixed(2)),
            averageHours: parseFloat((weekTotal / 60 / currentWeek.length).toFixed(2)),
            displayWeek: `${formatDateMonth(weekStart)} - ${formatDateMonth(currentWeek[currentWeek.length - 1].date)}`,
          });
        }
        currentWeek = [day];
        weekStart = day.date;
      } else {
        currentWeek.push(day);
      }
    });

    if (currentWeek.length > 0) {
      const weekTotal = currentWeek.reduce((sum, d) => sum + d.minutes, 0);
      weeks.push({
        weekStart: weekStart,
        weekEnd: currentWeek[currentWeek.length - 1].date,
        totalMinutes: weekTotal,
        totalHours: parseFloat((weekTotal / 60).toFixed(2)),
        averageHours: parseFloat((weekTotal / 60 / currentWeek.length).toFixed(2)),
        displayWeek: `${formatDateMonth(weekStart)} - ${formatDateMonth(currentWeek[currentWeek.length - 1].date)}`,
      });
    }

    return weeks;
  }, [chartData]);

  const formatTooltipValue = (value, name) => {
    if (name === 'hours') {
      return [`${value} год`, 'Робочих годин'];
    }
    return [value, name];
  };

  const formatYAxisTick = (value) => {
    return `${value}г`;
  };

  return (
    <div className={styles.chartContainer}>
      <h3>
        <FontAwesomeIcon icon={faChartLine} />
        Графік робочого часу
      </h3>

      <div className={styles.chartTabs}>
        <div className={styles.chartSection}>
          <h4 className={styles.chartTitle}>
            <FontAwesomeIcon icon={faCalendarAlt} />
            По днях
          </h4>
          <div className={styles.chartWrapper}>
            <ResponsiveContainer width='100%' height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='displayDate' tick={{ fontSize: 12 }} textAnchor='middle' interval='preserveStartEnd' />
                <YAxis width={40} tick={{ fontSize: 12 }} tickFormatter={formatYAxisTick} />
                <Tooltip
                  formatter={formatTooltipValue}
                  labelFormatter={(label) => `Дата: ${label}`}
                  contentStyle={{
                    padding: '0.8rem',
                    fontSize: 12,
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                  }}
                />
                <Bar dataKey='hours' fill='#384bc7' radius={[8, 8, 0, 0]} name='hours' />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {weeklyData.length > 0 && (
          <div className={styles.chartSection}>
            <h4 className={styles.chartTitle}>
              <FontAwesomeIcon icon={faChartLine} />
              По тижнях
            </h4>
            <div className={styles.chartWrapper}>
              <ResponsiveContainer width='100%' height={300}>
                <LineChart data={weeklyData}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis
                    dataKey='displayWeek'
                    tick={{ fontSize: 12 }}
                    textAnchor='middle'
                    interval='preserveStartEnd'
                  />
                  <YAxis width={40} tick={{ fontSize: 12 }} tickFormatter={formatYAxisTick} />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === 'totalHours') return [`${value} год`, 'Всього годин'];
                      if (name === 'averageHours') return [`${value} год`, 'Середньо за день'];
                      return [value, name];
                    }}
                    labelFormatter={(label) => `Тиждень: ${label}`}
                    contentStyle={{
                      padding: '0.8rem',
                      fontSize: 12,
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                    }}
                  />
                  <Line
                    type='monotone'
                    dataKey='totalHours'
                    stroke='#384bc7'
                    strokeWidth={2}
                    dot={{ fill: '#384bc7', strokeWidth: 2, r: 4 }}
                    name='totalHours'
                  />
                  <Line
                    type='monotone'
                    dataKey='averageHours'
                    stroke='#6c757d'
                    strokeWidth={2}
                    strokeDasharray='5 5'
                    dot={{ fill: '#6c757d', strokeWidth: 2, r: 3 }}
                    name='averageHours'
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <Stats data={chartData} />
      </div>
    </div>
  );
};
